import { patientProgramsManager, programManager } from "@core/service-container";
import { Patient, PendingPatient } from "@domain/patient/patient";
import { AllPatientPrograms } from "@domain/program/patient-program";
import { Program } from "@domain/program/program";
import { GlobalProgramStatusType } from "@domain/program/program-status/program-status";
import { extractGlobalProgramStatusFromPatient } from "@domain/program/program-status/program-status-utils";
import _ from "lodash";
import { useComputedObservable } from "micro-observables";
import { useCallback, useEffect, useMemo, useState } from "react";

export function usePatientProgram({
	programId,
	patientId,
	withFetch = true,
}: {
	programId?: string;
	patientId: string;
	withFetch?: boolean;
}): {
	loading: boolean;
	program: Program | undefined;
	error: unknown;
	refreshProgram: (patientId: string, programId: string) => Promise<void>;
} {
	const [loading, setLoading] = useState(withFetch && !!programId);
	const [error, setError] = useState<unknown>();

	const program = useComputedObservable<Program | undefined>(
		() => (programId ? programManager.programsByPatient.get().get(patientId)?.get(programId) : undefined),
		[patientId, programId],
	);

	const refreshProgram = useCallback(async (patientIdToFetch: string, programIdToFetch: string) => {
		try {
			setLoading(true);
			await programManager.fetchPatientProgram(patientIdToFetch, programIdToFetch);
			setError(undefined);
		} catch (e) {
			setError(e);
			console.error(e);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		withFetch && programId && refreshProgram(patientId, programId);
	}, [patientId, programId, refreshProgram, withFetch]);

	return { loading, program, error, refreshProgram };
}

export function useAllPatientPrograms(
	patient: Patient | PendingPatient,
	withFetch = true,
): {
	loading: boolean;
	allPatientPrograms: AllPatientPrograms | undefined;
	error: unknown;
	shouldDisplayProgramEndingSoonAlert: boolean;
	shouldDisplayProgramEndingNoOngoingAlert: boolean;
	refreshAllPrograms: (patientId: string) => Promise<void>;
} {
	const [loading, setLoading] = useState(withFetch);
	const [error, setError] = useState<unknown>(undefined);

	const allPatientPrograms = useComputedObservable(
		() => patientProgramsManager.allProgramsByPatient.get().get(patient.id),
		[patient],
	);

	const refreshAllPrograms = useCallback(async (patientIdToFetch: string) => {
		try {
			await patientProgramsManager.fetchAllPatientPrograms(patientIdToFetch);
			setError(null);
		} catch (e) {
			setError(e);
			console.error(e);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		withFetch && refreshAllPrograms(patient.id);
	}, [patient, refreshAllPrograms, withFetch]);

	const { shouldDisplayProgramEndingSoonAlert, shouldDisplayProgramEndingNoOngoingAlert } = useMemo(() => {
		const globalProgramStatus = extractGlobalProgramStatusFromPatient({
			ongoingProgram: allPatientPrograms?.ongoingProgram,
			previousProgram: _.head(allPatientPrograms?.endedPrograms),
			nextProgram: _.head(allPatientPrograms?.scheduledPrograms),
		});

		return {
			shouldDisplayProgramEndingSoonAlert:
				globalProgramStatus.type === GlobalProgramStatusType.EndingSoonProgramWithNoNext,
			shouldDisplayProgramEndingNoOngoingAlert: globalProgramStatus.type === GlobalProgramStatusType.EndedLastProgram,
		};
	}, [allPatientPrograms]);

	return {
		loading,
		allPatientPrograms,
		error,
		shouldDisplayProgramEndingSoonAlert,
		shouldDisplayProgramEndingNoOngoingAlert,
		refreshAllPrograms,
	};
}
